/**
 * Kraken REST API Client
 * @see https://docs.kraken.com/rest/
 */

import type {
  KrakenAssetPairsResponse,
  KrakenDepthResponse,
  KrakenOhlcResponse,
  KrakenTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.kraken.com';

/**
 * Kraken REST API endpoints.
 * @see https://docs.kraken.com/rest/
 */
export const krakenRestEndpoints = {
  ticker: `${BASE_URL}/0/public/Ticker`,
  depth: `${BASE_URL}/0/public/Depth`,
  ohlc: `${BASE_URL}/0/public/OHLC`,
  trades: `${BASE_URL}/0/public/Trades`,
  assetPairs: `${BASE_URL}/0/public/AssetPairs`,
} as const;

// ========================================
// Symbol Formatting Helpers
// ========================================

/**
 * Formats base/quote to Kraken REST symbol format.
 * REST API uses uppercase concatenated format (e.g., "BTCUSD", "ETHUSD").
 * Note: Some pairs use X/Z prefixes (e.g., XXBTZUSD for BTC/USD).
 *
 * @example formatKrakenSymbol("BTC", "USD") => "BTCUSD"
 */
export const formatKrakenSymbol = (base: string, quote: string): string => {
  return `${base.toUpperCase()}${quote.toUpperCase()}`;
};

/**
 * Formats base/quote to Kraken WebSocket V2 symbol format.
 * WebSocket uses slash-separated format (e.g., "BTC/USD", "ETH/USD").
 *
 * @example formatKrakenWsSymbol("BTC", "USD") => "BTC/USD"
 */
export const formatKrakenWsSymbol = (base: string, quote: string): string => {
  return `${base.toUpperCase()}/${quote.toUpperCase()}`;
};

/**
 * Parses Kraken's X-prefixed symbols to standard base/quote.
 * Kraken uses X/Z prefixes for crypto/fiat (e.g., XXBTZUSD => BTC/USD).
 *
 * @example parseKrakenSymbol("XXBTZUSD") => { base: "XBT", quote: "USD" }
 * @example parseKrakenSymbol("BTCUSD") => { base: "BTC", quote: "USD" }
 */
export const parseKrakenSymbol = (symbol: string): { base: string; quote: string } | null => {
  // Common quote currencies
  const quotes = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'USDT', 'USDC', 'DAI'];

  for (const quote of quotes) {
    // Check for Z-prefixed quote (e.g., ZUSD, ZEUR)
    if (symbol.endsWith(`Z${quote}`) || symbol.endsWith(quote)) {
      const quoteLen = symbol.endsWith(`Z${quote}`) ? quote.length + 1 : quote.length;
      let base = symbol.slice(0, -quoteLen);

      // Remove X prefix if present (e.g., XXBT => XBT)
      if (base.startsWith('X') && base.length > 3) {
        base = base.slice(1);
      }

      return { base, quote };
    }
  }

  return null;
};

/**
 * Validates Kraken API response for errors.
 * Throws if error array is non-empty.
 */
export const validateKrakenResponse = <T extends { error: string[] }>(response: T): T => {
  if (response.error && response.error.length > 0) {
    throw new Error(`Kraken API error: ${response.error.join(', ')}`);
  }
  return response;
};

// ========================================
// Ticker Types and Endpoint
// ========================================

export interface KrakenTickerRequest {
  /** Trading pair (e.g., XBTUSD, ETHUSD) */
  pair: string;
}

export interface KrakenTickerResponse {
  result: {
    [pair: string]: {
      /** Ask array [price, whole lot volume, lot volume] */
      a: string[];
      /** Bid array [price, whole lot volume, lot volume] */
      b: string[];
      /** Last trade closed array [price, lot volume] */
      c: string[];
      /** Volume array [today, last 24 hours] */
      v: string[];
      /** Volume weighted average price array [today, last 24 hours] */
      p: string[];
      /** Number of trades array [today, last 24 hours] */
      t: number[];
      /** Low array [today, last 24 hours] */
      l: string[];
      /** High array [today, last 24 hours] */
      h: string[];
      /** Today's opening price */
      o: string;
    };
  };
  error: string[];
}

/**
 * Fetches ticker information for a trading pair.
 * Note: Use index [1] for 24h values from arrays (h, l, v, p)
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getTickerInformation
 */
export const getKrakenTicker = async (
  params: KrakenTickerRequest,
  options?: RestRequestOptions,
): Promise<KrakenTickerResponse> => {
  const response = await get<KrakenTickerResponse, KrakenTickerRequest>({
    url: krakenRestEndpoints.ticker,
    params,
    options,
  });

  return validateKrakenResponse(response.data);
};

// ========================================
// Depth (Orderbook) Endpoint
// ========================================

export interface KrakenDepthRequest {
  /** Trading pair (e.g., XBTUSD) */
  pair: string;
  /** Maximum number of levels (1-500, default 100) */
  count?: number;
}

/**
 * Fetches orderbook depth for a trading pair.
 * Returns asks and bids as [price, volume, timestamp] tuples.
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getOrderBook
 */
export const getKrakenDepth = async (
  params: KrakenDepthRequest,
  options?: RestRequestOptions,
): Promise<KrakenDepthResponse> => {
  const response = await get<KrakenDepthResponse, KrakenDepthRequest>({
    url: krakenRestEndpoints.depth,
    params,
    options,
  });

  return validateKrakenResponse(response.data);
};

// ========================================
// OHLC (Klines) Endpoint
// ========================================

export interface KrakenOhlcRequest {
  /** Trading pair (e.g., XBTUSD) */
  pair: string;
  /** Time frame interval in minutes: 1, 5, 15, 30, 60, 240, 1440, 10080, 21600 */
  interval?: number;
  /** Return data since given timestamp (exclusive) */
  since?: number;
}

/**
 * Fetches OHLC (candlestick) data for a trading pair.
 * Returns array of [time, open, high, low, close, vwap, volume, count] tuples.
 * Timestamps are in SECONDS (multiply by 1000 for milliseconds).
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getOHLCData
 */
export const getKrakenOhlc = async (
  params: KrakenOhlcRequest,
  options?: RestRequestOptions,
): Promise<KrakenOhlcResponse> => {
  const response = await get<KrakenOhlcResponse, KrakenOhlcRequest>({
    url: krakenRestEndpoints.ohlc,
    params,
    options,
  });

  return validateKrakenResponse(response.data);
};

// ========================================
// Trades Endpoint
// ========================================

export interface KrakenTradesRequest {
  /** Trading pair (e.g., XBTUSD) */
  pair: string;
  /** Return trade data since given timestamp (exclusive) */
  since?: string;
  /** Number of trades to return (max 1000) */
  count?: number;
}

/**
 * Fetches recent trades for a trading pair.
 * Returns array of [price, volume, time, side, type, misc, trade_id] tuples.
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getRecentTrades
 */
export const getKrakenTrades = async (
  params: KrakenTradesRequest,
  options?: RestRequestOptions,
): Promise<KrakenTradesResponse> => {
  const response = await get<KrakenTradesResponse, KrakenTradesRequest>({
    url: krakenRestEndpoints.trades,
    params,
    options,
  });

  return validateKrakenResponse(response.data);
};

// ========================================
// Asset Pairs Endpoint
// ========================================

export interface KrakenAssetPairsRequest {
  /** Comma-delimited list of pairs (optional, all pairs if omitted) */
  pair?: string;
  /** Info level: "info" (default), "leverage", "fees", "margin" */
  info?: string;
}

/**
 * Fetches tradable asset pair information.
 * Useful for getting tick size (pair_decimals) and other trading parameters.
 * @see https://docs.kraken.com/rest/#tag/Market-Data/operation/getTradableAssetPairs
 */
export const getKrakenAssetPairs = async (
  params?: KrakenAssetPairsRequest,
  options?: RestRequestOptions,
): Promise<KrakenAssetPairsResponse> => {
  const response = await get<KrakenAssetPairsResponse, KrakenAssetPairsRequest | undefined>({
    url: krakenRestEndpoints.assetPairs,
    params,
    options,
  });

  return validateKrakenResponse(response.data);
};
